const textResult = text => ({ content: [{ type: 'text', text }] });

const errorResult = text => ({
  content: [{ type: 'text', text }],
  isError: true,
});

const createDateOnly = (yearValue, monthValue, dayValue) => {
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const utcTime = Date.UTC(year, month - 1, day);
  const utcDate = new Date(utcTime);

  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  return {
    utcTime,
    localDate: new Date(year, month - 1, day),
  };
};

const parseIsoDate = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? createDateOnly(match[1], match[2], match[3]) : null;
};

const parseRangeDate = value => {
  const isoDate = parseIsoDate(value);
  if (isoDate) {
    return isoDate;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  return match ? createDateOnly(match[3], match[1], match[2]) : null;
};

/**
 * Individual aliases for a destination. The API packs them into one
 * comma-separated string, e.g. France carries `Paris` and Cruise Ship carries
 * every cruise-line name.
 * @param {{ alias?: string }} destination - Destination entry from the API
 * @returns {string[]} Trimmed alias values, empty when the entry has none
 */
const aliasList = destination =>
  String(destination?.alias ?? '')
    .split(',')
    .map(alias => alias.trim())
    .filter(Boolean);

/**
 * Resolves a caller-supplied destination to a page entry by name or alias.
 * Cities, cruise lines, and airlines are aliases rather than destinations of
 * their own, so `Paris` has to land on `France` the same way the Autocomplete
 * resolves it — otherwise a caller that uses the customer's own wording is
 * rejected outright.
 * @param {Array<{ name?: string, alias?: string }>} destinations - Available destinations
 * @param {string} value - Destination name or alias to resolve
 * @returns {object | undefined} The matching entry, or undefined
 */
const matchDestination = (destinations, value) => {
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!text) {
    return undefined;
  }
  const byName = destinations.find(
    item => String(item?.name ?? '').toLowerCase() === text
  );
  if (byName) {
    return byName;
  }
  return destinations.find(item =>
    aliasList(item).some(alias => alias.toLowerCase() === text)
  );
};

export const buildDestinationTools = ({
  destinations = [],
  selectedDestinations = [],
  onAddDestinations,
  onRemoveDestination,
  onContinue,
}) => {
  if (destinations.length === 0) {
    return [];
  }

  const destinationNames = destinations.map(destination => destination.name);
  const selectedNames = selectedDestinations.map(destination => destination.name);

  return [
    {
      name: 'add_trip_destination',
      description: 'Adds an available destination to the international trip.',
      inputSchema: {
        type: 'object',
        properties: {
          destination: {
            type: 'string',
            description: 'Exact destination name offered by the page.',
            enum: destinationNames,
          },
        },
        required: ['destination'],
      },
      execute: ({ destination }) => {
        const match = matchDestination(destinations, destination);
        if (
          !match ||
          selectedDestinations.some(item => item.key === match.key)
        ) {
          return errorResult(
            'Choose an available destination that is not already selected.'
          );
        }
        onAddDestinations([...selectedDestinations, match]);
        // Report the resolved name, not the caller's wording, so a request for
        // `Paris` is echoed back as the `France` the page actually selected.
        return textResult(`Added ${match.name} to the trip.`);
      },
    },
    {
      name: 'remove_trip_destination',
      description: 'Removes a selected destination from the international trip.',
      inputSchema: {
        type: 'object',
        properties: {
          destination: {
            type: 'string',
            description: 'Exact selected destination name.',
            enum: selectedNames,
          },
        },
        required: ['destination'],
      },
      execute: ({ destination }) => {
        const match = matchDestination(selectedDestinations, destination);
        if (!match) {
          return errorResult('Choose a currently selected destination.');
        }
        onRemoveDestination(match);
        return textResult(`Removed ${match.name} from the trip.`);
      },
    },
    {
      name: 'continue_to_travel_dates',
      description: 'Saves the selected destinations and continues to travel dates.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        if (selectedDestinations.length === 0) {
          return errorResult('Select at least one destination before continuing.');
        }
        onContinue();
        return textResult('Saved destinations and continued to travel dates.');
      },
    },
  ];
};

export const buildTravelDateTools = ({
  travelDatesLimit,
  disableNext,
  onDatesSelected,
  onContinue,
}) => {
  if (!travelDatesLimit) {
    return [];
  }

  const minDate = parseRangeDate(travelDatesLimit.min);
  const maxDate = parseRangeDate(travelDatesLimit.max);

  return [
    {
      name: 'set_travel_dates',
      description: 'Sets the international trip start and end dates.',
      inputSchema: {
        type: 'object',
        properties: {
          startDate: {
            type: 'string',
            description: 'Trip start date in YYYY-MM-DD format.',
          },
          endDate: {
            type: 'string',
            description: 'Trip end date in YYYY-MM-DD format.',
          },
        },
        required: ['startDate', 'endDate'],
      },
      execute: ({ startDate, endDate }) => {
        const start = parseIsoDate(startDate);
        const end = parseIsoDate(endDate);
        if (
          !start ||
          !end ||
          !minDate ||
          !maxDate ||
          start.utcTime < minDate.utcTime ||
          start.utcTime > maxDate.utcTime ||
          end.utcTime <= start.utcTime ||
          end.utcTime > maxDate.utcTime
        ) {
          return errorResult(
            'Choose valid travel dates within the range offered by the page.'
          );
        }
        onDatesSelected({
          selection: {
            startDate: start.localDate,
            endDate: end.localDate,
            key: 'selection',
          },
        });
        return textResult(
          `Set travel dates from ${startDate} through ${endDate}.`
        );
      },
    },
    {
      name: 'continue_to_devices',
      description:
        'Saves the selected travel dates and continues to device and plan selection.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        if (disableNext) {
          return errorResult(
            'Select a valid start and end date before continuing.'
          );
        }
        onContinue();
        return textResult('Saved travel dates and continued to devices.');
      },
    },
  ];
};

export const buildEffectiveDateTools = ({
  minDate,
  maxDate,
  onDateSelected,
  onRecurringChange,
  onContinue,
}) => {
  if (!minDate || !maxDate) {
    return [];
  }

  const min = parseRangeDate(minDate);
  const max = parseRangeDate(maxDate);

  return [
    {
      name: 'set_itp_effective_date',
      description:
        'Sets the effective date and recurring option for the selected international travel plan.',
      inputSchema: {
        type: 'object',
        properties: {
          effectiveDate: {
            type: 'string',
            description:
              'Effective date in YYYY-MM-DD format within the range offered by the page.',
          },
          recurring: {
            type: 'boolean',
            description:
              'Use the recurring monthly plan option. Omit to leave the option as the page has it.',
          },
        },
        required: ['effectiveDate'],
      },
      execute: ({ effectiveDate, recurring }) => {
        const selected = parseIsoDate(effectiveDate);
        const changesRecurring = recurring !== undefined;
        if (
          !selected ||
          !min ||
          !max ||
          selected.utcTime < min.utcTime ||
          selected.utcTime > max.utcTime ||
          (changesRecurring && typeof recurring !== 'boolean')
        ) {
          return errorResult(
            'Choose a valid effective date and recurring option.'
          );
        }
        onDateSelected(selected.localDate);
        if (changesRecurring) {
          onRecurringChange(recurring);
        }
        return textResult(`Set the effective date to ${effectiveDate}.`);
      },
    },
    {
      name: 'continue_from_itp_effective_date',
      description:
        'Submits the effective date shown on the page and continues the trip flow.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        onContinue();
        return textResult('Submitted the effective date and continued.');
      },
    },
  ];
};

export const buildOptionalPlanTools = ({
  plans = [],
  onSelectPlan,
  onSkip,
}) => {
  if (plans.length === 0) {
    return [];
  }

  const planNames = plans.map(plan => plan.planName);

  return [
    {
      name: 'select_optional_itp_plan',
      description: 'Selects an available cruise or in-flight travel option.',
      inputSchema: {
        type: 'object',
        properties: {
          plan: {
            type: 'string',
            description: 'Exact optional plan name shown on the page.',
            enum: planNames,
          },
        },
        required: ['plan'],
      },
      execute: ({ plan }) => {
        if (typeof plan !== 'string' || !planNames.includes(plan)) {
          return errorResult('Choose an available optional travel plan.');
        }
        onSelectPlan(plan);
        return textResult(`Selected ${plan}.`);
      },
    },
    {
      name: 'skip_optional_itp_plan',
      description: 'Skips the optional cruise and in-flight plan step.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        onSkip();
        return textResult('Skipped the optional travel-plan step.');
      },
    },
  ];
};

export const buildTravelPlanTools = ({ plans = [], isDisabled, onSelectPlan }) => {
  const eligiblePlans = plans.filter(plan => !isDisabled(plan));
  const planNames = eligiblePlans.map(plan => plan.planName);
  if (planNames.length === 0) {
    return [];
  }

  return [
    {
      name: 'select_travel_plan',
      description:
        'Selects an eligible international travel plan shown on the current page.',
      inputSchema: {
        type: 'object',
        properties: {
          plan: {
            type: 'string',
            description: 'Exact eligible plan name shown on the page.',
            enum: planNames,
          },
        },
        required: ['plan'],
      },
      execute: ({ plan }) => {
        const selectedPlan = eligiblePlans.find(
          candidate => candidate.planName === plan
        );
        if (!selectedPlan) {
          return errorResult('Choose a currently eligible travel plan.');
        }
        onSelectPlan(selectedPlan);
        return textResult(`Selected ${selectedPlan.planName}.`);
      },
    },
  ];
};

// The unauthenticated list is a device catalog with no account identifier, so
// labels come from whichever names the page has for the device.
const describeDevice = device =>
  [device.brandName, device.deviceDisplayName || device.deviceImageAlt]
    .filter(Boolean)
    .join(' ')
    .trim();

// Devices can repeat a brand and display name, so labels are disambiguated to
// keep every device reachable through the enum.
const mapDeviceIndexesByLabel = devices => {
  const indexByLabel = new Map();

  devices.forEach((device, index) => {
    const base = describeDevice(device) || `Device ${index + 1}`;
    let label = base;
    let suffix = 2;
    while (indexByLabel.has(label)) {
      label = `${base} (${suffix})`;
      suffix += 1;
    }
    indexByLabel.set(label, index);
  });

  return indexByLabel;
};

const eligibleLineDevices = devices =>
  devices.filter(
    device =>
      device &&
      device.mtn &&
      device.hasPendingOrder !== true &&
      device.hasMultiPendingOrder !== true &&
      device.restrictedDevice !== true
  );

export const buildUnauthDeviceTools = ({
  devices = [],
  hasSelectedDevice,
  onSelectDevice,
  onContinue,
}) => {
  if (devices.length === 0) {
    return [];
  }

  const deviceIndexesByLabel = mapDeviceIndexesByLabel(devices);
  const deviceNames = [...deviceIndexesByLabel.keys()];

  return [
    {
      name: 'select_unauth_device',
      description:
        'Selects a device for the unauthenticated international trip flow.',
      inputSchema: {
        type: 'object',
        properties: {
          device: {
            type: 'string',
            description: 'Exact device label shown by the page.',
            enum: deviceNames,
          },
        },
        required: ['device'],
      },
      execute: ({ device }) => {
        const index = deviceIndexesByLabel.get(device);
        if (index === undefined) {
          return errorResult('Choose a device offered by the page.');
        }
        onSelectDevice(devices[index], index);
        return textResult(`Selected ${device}.`);
      },
    },
    {
      name: 'continue_unauth_device',
      description: 'Continues after selecting a device.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        if (!hasSelectedDevice) {
          return errorResult('Select a device before continuing.');
        }
        onContinue();
        return textResult('Continued with the selected device.');
      },
    },
  ];
};

export const buildDevicePlanTools = ({
  devices,
  stagedPlanChanges = [],
  isBlocked,
  onConfirm,
  onOpenPlanForLine,
}) => {
  if (!devices) {
    return [];
  }

  const eligibleDevices = eligibleLineDevices(devices);
  const lineMtns = eligibleDevices.map(device => String(device.mtn));
  const tools = [
    {
      name: 'get_trip_plan_status',
      description:
        'Returns the devices and staged international plan changes on the trip.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: () =>
        textResult(
          JSON.stringify({
            devices: devices.map(device => ({
              name: device.deviceNickName ?? device.deviceName ?? device.mtn,
              mtn: device.formattedMtn ?? device.mtn,
            })),
            stagedPlanChanges: stagedPlanChanges.map(plan => ({
              mtn: plan.mtn,
              plans: (plan.updatedPlanDetails ?? []).map(item => item.planName),
              effectiveDate: plan.effectiveDate,
            })),
          })
        ),
    },
    {
      name: 'confirm_trip_plans',
      description:
        'Continues to confirmation with the currently staged trip plan changes.',
      inputSchema: { type: 'object', properties: {} },
      execute: () => {
        if (isBlocked()) {
          return errorResult(
            'The current devices have blocking conditions and cannot continue.'
          );
        }
        onConfirm();
        return textResult('Continued to trip-plan confirmation.');
      },
    },
  ];

  if (typeof onOpenPlanForLine === 'function' && lineMtns.length > 0) {
    tools.push({
      name: 'open_plan_for_line',
      description:
        'Opens plan selection for an eligible line on the current trip.',
      inputSchema: {
        type: 'object',
        properties: {
          mtn: {
            type: 'string',
            description: 'Exact mobile number of an eligible line.',
            enum: lineMtns,
          },
        },
        required: ['mtn'],
      },
      execute: ({ mtn }) => {
        const selected = eligibleDevices.find(
          device =>
            String(device.mtn) === String(mtn) ||
            String(device.formattedMtn) === String(mtn)
        );
        if (!selected) {
          return errorResult('Choose an eligible line offered by the page.');
        }
        onOpenPlanForLine(selected);
        return textResult(`Opened the travel plan for ${selected.mtn}.`);
      },
    });
  }

  return tools;
};

export const ITP_STATE_CHANGED_EVENT = 'itp:stateChanged';

export const emitItpStateChanged = () => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }
  window.dispatchEvent(new CustomEvent(ITP_STATE_CHANGED_EVENT));
};

const padDatePart = value => String(value).padStart(2, '0');

const formatLocalDate = date =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate()
  )}`;

const toIsoDate = value => {
  if (!value) {
    return '';
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalDate(value);
  }
  if (typeof value !== 'string') {
    return '';
  }
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const parsed = parseRangeDate(value);
  return parsed ? formatLocalDate(parsed.localDate) : '';
};

export const itineraryStepFromPath = (pathname = '') => {
  const path = String(pathname).toLowerCase();
  if (path.includes('traveldates')) {
    return 'travel-dates';
  }
  if (path.includes('devicesandplans') || path.includes('uaitpdevices')) {
    return 'devices';
  }
  if (path.includes('cruiseplan')) {
    return 'cruise-plan';
  }
  if (path.includes('planselection') || path.includes('uaitpplan')) {
    return 'plan-selection';
  }
  if (path.includes('skipplan')) {
    return 'optional-plan';
  }
  if (path.includes('effectivedate')) {
    return 'effective-date';
  }
  if (path.includes('confirmation') || path.includes('uasummary')) {
    return 'confirmation';
  }
  return 'destination';
};

export const buildTripPlannerSnapshot = ({
  step,
  destinations = [],
  travelDates,
  selectedLine,
  stagedPlanChanges = [],
  confirmed = false,
} = {}) => {
  const line = selectedLine && typeof selectedLine === 'object' ? selectedLine : {};
  const mtn = line.mtn ? String(line.mtn) : '';
  return {
    step: step || 'destination',
    destinations: (Array.isArray(destinations) ? destinations : [])
      .filter(Boolean)
      .map(destination => ({
        key: destination.key ?? destination.code ?? '',
        name: destination.name ?? '',
      }))
      .filter(destination => destination.key || destination.name),
    travelDates: {
      startDate: toIsoDate(
        travelDates?.startDate ?? travelDates?.travelStartDate
      ),
      endDate: toIsoDate(travelDates?.endDate ?? travelDates?.travelEndDate),
    },
    selectedLine: mtn
      ? {
          mtn,
          displayName:
            line.deviceNickName ||
            line.deviceName ||
            line.displayName ||
            line.formattedMtn ||
            '',
        }
      : null,
    stagedPlanChanges: (Array.isArray(stagedPlanChanges)
      ? stagedPlanChanges
      : []
    ).map(plan => ({
      mtn: plan.mtn ?? '',
      planId:
        plan.updatedPlanDetails?.[0]?.planId ??
        plan.planId ??
        '',
      planName:
        plan.updatedPlanDetails?.[0]?.planName ??
        plan.planName ??
        '',
      effectiveDate: toIsoDate(plan.effectiveDate),
      recurring: Boolean(plan.recurring),
    })),
    confirmed: Boolean(confirmed),
  };
};

export const buildTripPlannerStateTool = getSnapshot => ({
  name: 'get_trip_planner_state',
  description: 'Returns the current international trip planner state.',
  inputSchema: { type: 'object', properties: {} },
  annotations: { readOnlyHint: true },
  execute: () => {
    try {
      const snapshot =
        typeof getSnapshot === 'function' ? getSnapshot() : getSnapshot;
      return textResult(JSON.stringify(snapshot ?? {}));
    } catch (error) {
      return errorResult('Unable to read the current trip planner state.');
    }
  },
});

export const buildConfirmationTools = ({
  confirmation,
  destinations = [],
  travelDates,
  confirmedPlans = [],
}) => {
  if (!confirmation) {
    return [];
  }

  return [
    {
      name: 'get_trip_summary',
      description:
        'Returns the completed international trip and plan-change summary.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: () =>
        textResult(
          JSON.stringify({
            destinations: destinations.map(destination => destination.name),
            travelDates,
            plans: confirmedPlans.map(plan => ({
              device: plan.deviceNickName ?? plan.formattedMtn,
              plans: (plan.updatedPlanDetails ?? []).map(item => item.planName),
              effectiveDate: plan.effectiveDate,
            })),
            confirmation: {
              heading: confirmation.heading,
              message: confirmation.subHeadingText,
            },
          })
        ),
    },
  ];
};
